import type { TIncomingCall } from "../../types/callCenter.types";
import {
  mapMvoiceEndedPayload,
  mapMvoiceIncomingPayload,
} from "../mvoice/mapMvoicePayloads";

function asObject(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

function pickStr(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function pickCallId(o: Record<string, unknown>): string | undefined {
  return (
    pickStr(o, "callId") ??
    pickStr(o, "uniqueId") ??
    pickStr(o, "uniqueid")
  );
}

function pickNum(o: Record<string, unknown>, key: string): number | undefined {
  const v = o[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** PaycallCallStart / GET /Paycall/active-calls — normalize to MVoice incoming shape. */
export function mapPaycallStartPayload(payload: unknown): TIncomingCall | null {
  const o = asObject(payload);
  if (!o) return null;

  const callId = pickCallId(o);
  if (!callId) return null;

  const phone = pickStr(o, "phone") ?? pickStr(o, "caller") ?? callId;
  const direction = pickStr(o, "direction") ?? pickStr(o, "call_direction");
  const isOutbound = direction?.toLowerCase() === "outbound";

  const mapped = mapMvoiceIncomingPayload({
    ...o,
    eventType: "incoming",
    callId,
    phone,
  });

  if (!mapped) return null;
  return isOutbound ? { ...mapped, isOutbound: true } : mapped;
}

function pickDurationFromPaycallEnd(o: Record<string, unknown>): number | undefined {
  const fromBroadcast = pickNum(o, "totalDurationSeconds");
  if (fromBroadcast != null && fromBroadcast >= 0) {
    return Math.floor(fromBroadcast);
  }
  const totalDuration = pickStr(o, "total_duration");
  if (totalDuration) {
    const n = parseInt(totalDuration, 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

/** PaycallCallTaken — who answered and whether it was this agent's extension. */
export function mapPaycallCallTakenPayload(
  payload: unknown,
  myExtension: string | null
): { callId: string; answeredByMe: boolean; hasAnsweredAgent: boolean } | null {
  const o = asObject(payload);
  if (!o) return null;

  const callId = pickCallId(o);
  if (!callId) return null;

  // Do not fall back to `callee` — on inbound Paycall events it is the queue/DID (e.g. 1000), not the agent extension.
  const answeredByExten =
    pickStr(o, "answeredByExten") ?? pickStr(o, "exten");
  const hasAnsweredAgent = Boolean(answeredByExten);
  const answeredByMe = Boolean(
    answeredByExten &&
      myExtension &&
      String(answeredByExten) === String(myExtension)
  );

  return { callId, answeredByMe, hasAnsweredAgent };
}

/** PaycallCallEnd — maps PaycallCallEndEvent / backend broadcast to CDR. */
export function mapPaycallEndPayload(payload: unknown) {
  const o = asObject(payload);
  if (!o) return null;

  const callId = pickCallId(o);
  if (!callId) return null;

  const durationSeconds = pickDurationFromPaycallEnd(o);
  const recordingUrl =
    pickStr(o, "recordingUrl") ?? pickStr(o, "dlink") ?? pickStr(o, "multivoice_link");

  return mapMvoiceEndedPayload({
    ...o,
    callId,
    eventType: "ended",
    ...(durationSeconds != null ? { durationSeconds } : {}),
    ...(recordingUrl ? { recordingUrl } : {}),
    startedAt: pickStr(o, "startedAt") ?? pickStr(o, "start_time"),
    receivedAt: pickStr(o, "receivedAt") ?? pickStr(o, "end_time"),
    direction: pickStr(o, "direction") ?? pickStr(o, "call_direction"),
  });
}
